import { format, isToday, isYesterday, startOfDay, differenceInDays, isWeekend, subDays } from 'date-fns';
import { nl } from 'date-fns/locale';

export interface DayGroup<T> {
  date: Date;
  dateLabel: string;
  formattedDate: string;
  items: T[];
  count: number;
}

export function getRelativeDateLabel(date: Date): string {
  if (isToday(date)) {
    return 'Vandaag';
  }

  if (isYesterday(date)) {
    return 'Gisteren';
  }

  // For dates within the last week, show day name
  const daysDiff = differenceInDays(new Date(), date);
  if (daysDiff <= 7 && daysDiff > 0) {
    return format(date, 'EEEE', { locale: nl });
  }

  // For older dates, show full date
  return format(date, 'd MMMM yyyy', { locale: nl });
}

export function getFormattedDate(date: Date): string {
  return format(date, 'EEEE d MMMM', { locale: nl });
}

export function groupByDay<T>(
  items: T[],
  getDate: (item: T) => Date | string
): DayGroup<T>[] {
  // If no items provided and we need to create empty groups
  if (items.length === 0) {
    return [];
  }

  const groups = new Map<string, T[]>();

  // Group items by day
  items.forEach(item => {
    const itemDate = getDate(item);
    const date = typeof itemDate === 'string' ? new Date(itemDate) : itemDate;
    const dayKey = startOfDay(date).toISOString();

    if (!groups.has(dayKey)) {
      groups.set(dayKey, []);
    }
    groups.get(dayKey)!.push(item);
  });

  // Convert to array and sort by date (newest first)
  const dayGroups = Array.from(groups.entries())
    .map(([dateStr, items]) => {
      const date = new Date(dateStr);
      return {
        date,
        dateLabel: getRelativeDateLabel(date),
        formattedDate: getFormattedDate(date),
        items,
        count: items.length
      };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  return dayGroups;
}

export function createEmptyDayGroup<T>(date: Date): DayGroup<T> {
  return {
    date,
    dateLabel: getRelativeDateLabel(date),
    formattedDate: getFormattedDate(date),
    items: [],
    count: 0
  };
}

export function getDaysArray(numberOfDays: number = 5): Date[] {
  const days: Date[] = [];
  let currentDate = startOfDay(new Date());

  // Keep going back in time until we have the required number of weekdays
  while (days.length < numberOfDays) {
    // Only add weekdays (Monday-Friday)
    if (!isWeekend(currentDate)) {
      days.push(currentDate);
    }
    // Go back one day
    currentDate = subDays(currentDate, 1);
  }

  return days;
}

export function isSameDay(date1: Date | string, date2: Date | string): boolean {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;

  return startOfDay(d1).getTime() === startOfDay(d2).getTime();
}